import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { PermissionGate } from '../auth/PermissionGate';
import { Button } from './Button';
import { Tooltip } from './Tooltip';

/**
 * THE standard Create button — the one control that adds a record, wherever it is drawn.
 *
 * It exists because NINETEEN places drew it and only one of them was the definition.
 * `<DataTablePanel>` typed its own `onCreate` as a **ReactNode**, so fourteen panels hand-rolled
 * `PermissionGate → Tooltip → Button → Plus` for themselves; three surfaces outside the two
 * shells — the org chart, the tier ladder and the goal-setting tree — wrote the same stack again
 * because they are not `<IndexPage>`; and `<IndexToolbar>` itself wrote it twice, thirty lines
 * apart, for its plain and its menu variant.
 *
 * Eighteen copies is eighteen chances to get the icon, the variant, the gate or the disabled
 * reason wrong, and five of them did: four passed `<Tooltip block>` and took the whole toolbar
 * row with a `w-full` anchor, and one put its icon in the CHILDREN, where `<Button>`'s label
 * span and Tailwind preflight's `svg { display: block }` pushed the label onto a second line.
 *
 * So a surface declares WHAT the button does and never how it looks:
 *
 * ```tsx
 * onCreate={() => setCreating(true)}
 * createPermission="ppas.create"
 * createDisabledReason={lockedReason}
 * ```
 *
 * - **The icon is always `<Plus>` and the variant is always `primary`.** There is one primary
 *   action on a surface and it is the one that adds a record; a caller choosing its own glyph
 *   is how a product ends up with four different Create buttons.
 * - **THE LABEL IS `Create`, and an INDEX PAGE does not name its own.** Product call,
 *   29 Sep 2026. Every register in the product adds a record, so one word across all of them
 *   is what makes the control recognisable before it is read — and a per-page verb bought
 *   nothing a reader could not get from the breadcrumb and the list they are standing on.
 *   `createLabel` survives for two cases: an index page with a DEDICATED design, where the
 *   table is not the shared one and the noun is genuinely not what the rows are (Stock Levels
 *   is the only one), and a `<DataTablePanel>` inside a profile, where the button sits among a
 *   record's own sections rather than at the head of a register.
 * - **The MODAL still says what it is, and that is not two words for one act.** A toolbar
 *   reading Create over a dialog titled `Raise audit` is a generic affordance opening a named
 *   form; the rule it looks like it breaks is about one CONTROL spelled two ways.
 * - **`trailing` is for the MENU variant and nothing else.** `<IndexToolbar>` draws this same
 *   button as a dropdown trigger when a module has several things to create, and the chevron
 *   that says so is the one thing that ever sits after the label. It renders INSIDE a flex span
 *   with it, because `<Button>` puts its children in one `<span>` and Tailwind preflight sets
 *   `svg { display: block }` — an svg dropped in as inline content pushes the label onto a
 *   second line, which is the defect this component exists to make unrepeatable.
 * - **Hidden without the key, disabled for STATE with a reason.** A disabled button fires no
 *   pointer events, so the sentence comes from `<Tooltip disabledReason>`, which wraps the
 *   child in a focusable span — and that tooltip is NEVER `block`: the child is an inline-flex
 *   `<Button>`, and `block w-full` would claim the whole row it sits in.
 */
export function CreateButton({
  id,
  onClick,
  label = 'Create',
  permission,
  disabledReason,
  trailing,
}: {
  /** `${module}-create`, so a rejection or a test can name the control. */
  id: string;
  onClick: () => void;
  label?: string;
  /** Hides the button entirely when the caller lacks it. */
  permission?: string;
  /** Disables it and says why. Null or empty leaves it enabled. */
  disabledReason?: string | null;
  /** The menu variant's chevron. Nothing else belongs here — see the note above. */
  trailing?: ReactNode;
}) {
  return (
    <PermissionGate permission={permission}>
      <Tooltip disabledReason={disabledReason ?? null}>
        <Button
          id={id}
          variant="primary"
          leading={<Plus className="h-4 w-4" />}
          onClick={onClick}
          disabled={Boolean(disabledReason?.trim())}
        >
          {trailing ? (
            <span className="flex items-center gap-1.5">
              {label}
              {trailing}
            </span>
          ) : (
            label
          )}
        </Button>
      </Tooltip>
    </PermissionGate>
  );
}
